import { Contract, ethers, Signer } from "ethers";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useRouter } from "../../hooks/useRouter";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import { setEasterEggAvailability } from "../../store/reducers/auth";
import ABI from "./easterEggABI.json";
import ensSubdomainABI from "./ETHTaipeiSubdomain.json";
import { getSecrets } from "../../config";
interface IEasterEggContext {
  claimNFT: () => Promise<{
    detail: string;
  }>;
  isAvailabilityLoading: boolean;
}

export const easterEggContext = React.createContext<IEasterEggContext>({
  claimNFT: async () => {
    return {
      detail: "failed",
    };
  },
  isAvailabilityLoading: true,
});

interface IProps {
  children: React.ReactNode;
}

export const EasterEggProvider = ({ children }: IProps) => {
  const dispatch = useAppDispatch();
  const { roomUrl } = useWhiteboardRoom();
  const {
    RPC_ENDPOINT,
    ETH_TAIPEI_CHAIN_ID,
    ETH_TAIPEI_CONTRACT,
    EASTEREGG_CHAIN_ID,
    EASTEREGG_CONTRACT,
  } = getSecrets();
  const { provider, address } = useWeb3Auth();
  const useETHTaipeiSubdomain = roomUrl === "https://ethtaipei.org/agenda";
  const getContractAndSigner = async (rpcProvider: any) => {
    const ethProvider = new ethers.providers.Web3Provider(rpcProvider);
    const contractAddress = useETHTaipeiSubdomain
      ? ETH_TAIPEI_CONTRACT
      : EASTEREGG_CONTRACT;
    const contractABI = useETHTaipeiSubdomain ? ensSubdomainABI : ABI;
    const signer = ethProvider.getSigner();
    const contract = new ethers.Contract(
      contractAddress,
      JSON.stringify(contractABI),
      signer
    );
    return {
      contract,
      signer,
    };
  };
  const getETHTaipeiContract = async () => {
    const ethProvider = new ethers.providers.JsonRpcProvider(RPC_ENDPOINT);
    const contractABI = ensSubdomainABI;
    const contract = new ethers.Contract(
      ETH_TAIPEI_CONTRACT,
      JSON.stringify(contractABI),
      ethProvider
    );
    return {
      contract,
    };
  };
  const checkEasterEggAvailability = async (url: string) => {
    try {
      const { contract } = await getContractAndSigner(provider);
      const availability = await contract.claimable(url);
      return {
        detail: availability as boolean,
      };
    } catch (e) {
      console.error(e);
      return {
        detail: false,
      };
    }
  };
  const checkETHTaipeiSubdomainAvailability = async () => {
    const { contract } = await getETHTaipeiContract();
    const balanceOf = await contract.balanceOf(address);
    const totalSupply = await contract.totalSupply();
    return balanceOf.eq(0) && totalSupply.lt(101);
  };
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  useEffect(() => {
    setIsAvailabilityLoading(true);
    if (!provider) {
      setIsAvailabilityLoading(false);
      return;
    }
    if (useETHTaipeiSubdomain) {
      checkETHTaipeiSubdomainAvailability().then((availability) => {
        dispatch(setEasterEggAvailability(availability));
        setIsAvailabilityLoading(false);
      });
      return;
    }
    if (!roomUrl) {
      setIsAvailabilityLoading(false);
      return;
    }
    checkEasterEggAvailability(roomUrl).then(({ detail }) => {
      dispatch(setEasterEggAvailability(detail));
      setIsAvailabilityLoading(false);
    });
  }, [provider, roomUrl, useETHTaipeiSubdomain]);

  const claimNFT = async () => {
    if (!provider) return;
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [
          {
            chainId: useETHTaipeiSubdomain
              ? ETH_TAIPEI_CHAIN_ID
              : EASTEREGG_CHAIN_ID,
          },
        ], // chainId must be in hexadecimal numbers
      });
      const { contract, signer } = await getContractAndSigner(provider);

      const res = "success";

      const contractAddress = useETHTaipeiSubdomain
        ? ETH_TAIPEI_CONTRACT
        : EASTEREGG_CONTRACT;

      const chainId = await signer.getChainId();

      const domain = {
        name: "ETHTaipei - 101 Easter Eggs (2023)", // contract deploy name
        version: "1", // contract deploy version
        chainId: chainId, // env chain id
        verifyingContract: contractAddress,
      };
      const types = {
        Message: [{ name: "message", type: "string" }],
      };
      const signature = await signer._signTypedData(domain, types, {
        message:
          "Make a unique signature for validation to unlock the exclusive Easter Egg within the ETHTaipei 2023 event.",
      });

      // const signature = await signer.signMessage(ethers.utils.arrayify(hash));
      if (useETHTaipeiSubdomain)
        await contract.registerSubdomain("ethtaipei2023", signature, {});
      else await contract.mint(roomUrl, signature, { gasLimit: 400000 });
      return {
        detail: res,
      };
    } catch (e) {
      console.error(e);
      return {
        detail: JSON.stringify(e),
      };
    }
  };

  return (
    <easterEggContext.Provider value={{ claimNFT, isAvailabilityLoading }}>
      {children}
    </easterEggContext.Provider>
  );
};
